import React, { useState, memo, useEffect, useMemo } from "react";
import RunMetric from "./run-metric";
import RunQuality from "./run-quality";
import RunCyclePlots from "./run-cycleplots";
import RunDetails from "./run-details";
import RunEvents from "./run-events";
import RunTile from "./run-tile";
import PolonyDensity from "./run-tile-polony";
import RunLogs from "./run-logs";
import RunThumbnails from "./run-thumbnails";
import { Run, SpatialRun, TypeRun } from "../types";
import ValuesChart from "./run-valuesCharts";
import RunIndexing from "./run-indexing";
import { SelectBox } from "devextreme-react";
import RunSystemStats from "./run-systemstats";
import RunProcessStats from "./run-processstats";
import RunSSDStats from "./run-ssdstats";
import RunNotifications from "./run-notifications";
import RunBaseComposition from "./run-basecomposition";
import { fetchWithToken, useFetch } from "src/hooks/useFetch";
import { useAuth } from "src/contexts/auth";
import { isVitariModel } from "src/utils/telemetry-logs-path";
import { prodStatsTabDisabled, prodStatsTabSuffix } from "./prod-stats-tabs";
import type { ProdStatsTab } from "./prod-stats-tabs";
import RunTileCycle from "./run-tile-cycle";
import CycleChart from "./run-cycle-data";
import LegacyPolonyDensity from "./polony-density";
import RunProdstatsIndexing from "./run-prodstats-indexing";
import RunValuesLog from "./run-values-log";
import RunAthenaLogs from "./run-athena-logs";
import CytoPolonyCounts from "./run-cyto-polony-counts";
import CytoBarcodeAssigned from "./run-cyto-barcode-assigned";
import CytoCellAssigned, { PlotType } from "./run-cyto-cell-assigned";
import CytoRunThumbnail from "./run-cyto-thumbnails";
import CytoWellSegmentation from "./run-cyto-well-segmentation";
import CytoSegmentationThumbnail from "./run-cyto-segmentation-thumbnails";
import RunInstrumentReport from "./run-instrument-report";
import CytoCycleProgress from "./run-cyto-cycle-progress";
import CytoCycleTile from "./run-cyto-tile-cycle";
import CytoCycleProgressMovie from "./run-cyto-cycle-progress-movie";
import CytoUnassignedSequencesTable from "./run-cyto-unassigned-sequences";
import CytoRegistrationSuccess from "./run-cyto-registration-success";
import CytoTargetAssignmentStats from "./run-cyto-target-assignment-stats";
import CytoPolonyAssignment from "./run-cyto-polony-assignment";
import CytoQCDashboard from "./run-cyto-qc-dashboard";
import CytoCellPaint from "./run-cyto-cell-paint";

export interface TilesDataType {
  tileLabels: string[];
  rawPolonyCounts: string;
  pfPolonyCounts: string;
  avgQByCycle: string;
  q15ByCycle: string;
  q15AllByCycle: string;
  q30ByCycle: string;
  q40ByCycle: string;
  q50ByCycle: string;
  errorByCycle: string;
  intensityByCycle: string;
  backgroundByCycle: string;
  fwhmByCycle: string;
  phasingByCycle: string;
  prephasingByCycle: string;
}

export interface ProdStatsType {
  tiles: TilesDataType;
  polonyCountsInMByLane: number[];
  pfCountsInMByLane: number[];
  avgQByCycle: string;
  avgQByCycleByLane: string;
  indexingRunStats: IndexingStats;
  indexingLaneStats: IndexingStats[];
  alignedR1: number;
  alignedR2: number;
}

export interface IndexingStats {
  Lane: number;
  NumPolonies: number;
  AssignmentRate: number;
  MismatchRate: number;
  SampleIndex: IndexingSampleStats[];
}

export interface IndexingSampleStats {
  SampleName: string;
  SampleNumber: number;
  NumPolonies: number;
  MismatchRate: number;
}

type TelemetryRunContainerProps = {
  selectedRun: Run | SpatialRun;
  typeRun: TypeRun;
  defaultTab: number;
  tabSelected: (tab: string) => void;
};

const PRODSTATS_TABS: ProdStatsTab[] = [
  "Cycle Chart",
  "Polony Density Flow Cell",
  "Cycle Data Flow Cell",
  "Indexing Stats",
  "Values Log",
];

const getNumDaysSince = (date: Date) => {
  if (date === null) {
    return 90;
  }
  const origDate = new Date(date);
  const currDate = new Date();
  const timeDiff = currDate.getTime() - origDate.getTime();
  return Math.round(timeDiff / (1000 * 3600 * 24));
};

export default memo(
  ({ selectedRun, tabSelected, typeRun }: TelemetryRunContainerProps) => {
    const [prodStatsExists, setProdStatsExists] = useState(false);
    const { getAccessTokenSilently } = useAuth();

    const params = new URLSearchParams(
      window.location.hash.replace("#/runs", "")
    );

    const [selectedTab, setSelectedTab] = useState(
      params.get("t") || "Details"
    );

    useEffect(() => {
      // ProdStats are sequencing-only, and /run/{id}/prodStatsExists resolves the
      // id against the Run table: without this the first render requests
      // /run/undefined/prodStatsExists, and every spatial run requests an id that
      // table has no row for — both denied, and the result unused either way.
      if (!selectedRun?.runID || typeRun === "Spatial") return;
      fetchWithToken(
        getAccessTokenSilently,
        "/run/" + selectedRun?.runID + "/prodStatsExists"
      )
        .then((res: boolean) => {
          setProdStatsExists(res);
          if (!res) {
            setSelectedTab((prev) => {
              return PRODSTATS_TABS.includes(prev as ProdStatsTab) ? "" : prev;
            });
          }
        })
        .catch((err) => console.error(err));
    }, [selectedRun?.runID, typeRun, getAccessTokenSilently]);

    const { data: prodStatsData, isLoading } = useFetch(
      "/run/" + selectedRun?.runID + "/prodStats",
      prodStatsExists
    );

    const isVitari = isVitariModel(
      selectedRun?.model || selectedRun?.instrumentModel
    );

    useEffect(() => {
      // A ProdStats tab this run has no payload for (a Vitari run has only Polony Density) cannot
      // stay selected once the data is known. While the data is still undefined a deep link such as
      // ?t=Polony Density Flow Cell is kept, so it survives the loading period.
      if (!prodStatsData) return;
      setSelectedTab((prev) =>
        PRODSTATS_TABS.includes(prev as ProdStatsTab) &&
        prodStatsTabDisabled({
          tab: prev as ProdStatsTab,
          prodStatsExists,
          data: prodStatsData,
          isLoading: false,
          isVitari,
        })
          ? ""
          : prev
      );
    }, [prodStatsData, prodStatsExists, isVitari]);

    const spatialTabs = useMemo(() => {
      const addCategory = (
        category: string,
        items: { text: string; disabled?: boolean }[]
      ) => items.map((item) => ({ ...item, category }));

      return [
        {
          // Run Logs is deliberately absent: run-log ingestion is sequencing-only,
          // and /run/{id}/runLog resolves the id against the Run table, where a
          // spatial run has no row — so offering the tab here only produced a 401.
          key: "General",
          items: addCategory("General", [
            { text: "Details" },
            { text: "Events" },
          ]),
        },
        {
          key: "QC",
          items: addCategory("QC", [
            { text: "Run Review" },
            { text: "Polony Counts" },
            { text: "Well Segmentation Stats" },
            { text: "Registration Success" },
          ]),
        },
        {
          key: "QC Barcoding",
          items: addCategory("QC Barcoding", [
            { text: "Assigned Rate" },
            { text: "Mismatch Rate" },
            { text: "Cell Assigned Counts" },
            { text: "Cell Target Counts" },
            { text: "Cell Assigned Rate" },
            { text: "Cell Polony Dropout Rate" },
          ]),
        },
        {
          key: "QC OPS",
          items: addCategory("QC OPS", [{ text: "Target Assignment Stats" }]),
        },
        {
          key: "Cell Paint",
          items: addCategory("Cell Paint", [{ text: "Cell Paint" }]),
        },
        {
          key: "QC 3' Untargeted",
          items: addCategory("3' Untargeted", [{ text: "Polony Assigment" }]),
        },
        {
          key: "Cycle Progress",
          items: addCategory("Cycle Progress", [
            { text: "Cycle Progress Charts" },
            { text: "Cycle Progress Heatmap" },
            { text: "Cycle Progress Movie" },
          ]),
        },
        {
          key: "S3",
          items: addCategory("S3", [
            { text: "Files" },
            { text: "Well Thumbnails" },
            { text: "Segmentation Thumbnails" },
          ]),
        },
        {
          key: "Snowflake (slow)",
          items: addCategory("Snowflake", [
            { text: "Sensor Data" },
            { text: "System Stats" },
            { text: "SSD Stats" },
            { text: "Process Stats" },
          ]),
        },
      ];
    }, []);

    const tabs = useMemo(() => {
      const addCategory = (
        category: string,
        items: { text: string; disabled?: boolean }[]
      ) => items.map((item) => ({ ...item, category }));

      const prodStatsItem = (tab: ProdStatsTab) => {
        const gate = {
          tab,
          prodStatsExists,
          data: prodStatsData,
          isLoading,
          isVitari,
        };
        return {
          text: tab + prodStatsTabSuffix(gate),
          disabled: prodStatsTabDisabled(gate),
        };
      };

      return [
        {
          key: "General",
          items: addCategory("General", [
            { text: "Details" },
            { text: "Events" },
            { text: "Run Logs" },
          ]),
        },
        {
          key: "ProdStats Charts",
          items: addCategory("ProdStats", [
            prodStatsItem("Cycle Chart"),
            prodStatsItem("Polony Density Flow Cell"),
            prodStatsItem("Cycle Data Flow Cell"),
            prodStatsItem("Indexing Stats"),
          ]),
        },
        {
          key: "Values Log Charts",
          items: addCategory("Values Log", [prodStatsItem("Values Log")]),
        },
        {
          key: "Legacy Charts",
          items: addCategory("Legacy", [
            { text: "Quality & Error" },
            { text: "Base Composition" },
          ]),
        },
        {
          key: "S3",
          items: addCategory("S3", [
            { text: "Files" },
            { text: "Thumbnails" },
            { text: "Instrument Report" },
          ]),
        },
        {
          key: "Snowflake (slow)",
          items: addCategory("Snowflake", [
            { text: "Cycle Plots" },
            { text: "Sensor Data" },
            { text: "System Stats" },
            { text: "SSD Stats" },
            { text: "Process Stats" },
            { text: "Legacy Metrics" },
            { text: "Indexing" },
            { text: "Notifications" },
          ]),
        },
      ];
    }, [prodStatsData, isLoading, prodStatsExists, isVitari]);

    function getReadsInOrder(
      selectedRun: Run
    ): { read: string; cycles: number }[] {
      const ret = [];
      for (let i = 0; i < selectedRun?.readOrder.length; i += 2) {
        const read = selectedRun?.readOrder.slice(i, i + 2);
        const cycles = selectedRun[read.toLowerCase()];
        ret.push({ read, cycles });
      }
      return ret;
    }

    return (
      <div style={{ flex: "auto", margin: 30 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          Display:&nbsp;&nbsp;&nbsp;
          {typeRun === "Spatial" ? (
            <SelectBox
              width={300}
              grouped={true}
              valueExpr="text"
              displayExpr="text"
              fieldRender={(item) => (
                <div
                  className="dx-texteditor-input"
                  style={{ padding: "7px 9px" }}
                >
                  {item ? `${item.category}: ${item.text}` : ""}
                </div>
              )}
              items={spatialTabs}
              value={selectedTab}
              onValueChanged={(e) => {
                setSelectedTab(e.value);
                tabSelected(e.value);
              }}
            />
          ) : (
            <SelectBox
              width={340}
              grouped={true}
              valueExpr="text"
              displayExpr="text"
              fieldRender={(item) => (
                <div
                  className="dx-texteditor-input"
                  style={{ padding: "7px 9px" }}
                >
                  {item ? `${item.category}: ${item.text}` : ""}
                </div>
              )}
              items={tabs}
              value={selectedTab}
              onValueChanged={(e) => {
                setSelectedTab(e.value);
                tabSelected(e.value);
              }}
            />
          )}
        </div>
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <hr />
        </div>
        {selectedTab === "Details" && (
          <RunDetails run={selectedRun} typeRun={typeRun} />
        )}
        {selectedTab === "Run Review" && (
          <CytoQCDashboard run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Polony Counts" && (
          <CytoPolonyCounts run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Assigned Rate" && (
          <>
            <CytoBarcodeAssigned
              run={selectedRun as SpatialRun}
              type="assigned"
            />
            <CytoUnassignedSequencesTable run={selectedRun as SpatialRun} />
          </>
        )}
        {selectedTab === "Mismatch Rate" && (
          <CytoBarcodeAssigned
            run={selectedRun as SpatialRun}
            type="mismatch"
          />
        )}
        {selectedTab === "Cell Assigned Counts" && (
          <CytoCellAssigned
            run={selectedRun as SpatialRun}
            type={PlotType.CellAssignedCounts}
          />
        )}
        {selectedTab === "Cell Target Counts" && (
          <CytoCellAssigned
            run={selectedRun as SpatialRun}
            type={PlotType.CellTargetCounts}
          />
        )}
        {selectedTab === "Cell Assigned Rate" && (
          <>
            <CytoCellAssigned
              run={selectedRun as SpatialRun}
              type={PlotType.CellAssignedRate}
            />
          </>
        )}

        {selectedTab === "Registration Success" && (
          <CytoRegistrationSuccess run={selectedRun as SpatialRun} />
        )}

        {selectedTab === "Polony Assigment" && (
          <CytoPolonyAssignment run={selectedRun as SpatialRun} />
        )}

        {selectedTab === "Cell Paint" && (
          <CytoCellPaint run={selectedRun as SpatialRun} />
        )}

        {selectedTab === "Cell Polony Dropout Rate" && (
          <CytoCellAssigned
            run={selectedRun as SpatialRun}
            type={PlotType.CellPolonyDropoutRate}
          />
        )}
        {selectedTab === "Well Segmentation Stats" && (
          <CytoWellSegmentation run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Cycle Progress Charts" && (
          <CytoCycleProgress run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Cycle Progress Heatmap" && (
          <CytoCycleTile run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Cycle Progress Movie" && (
          <CytoCycleProgressMovie run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Events" && (
          <RunEvents
            runID={selectedRun?.runID}
            typeRun={typeRun}
            days={getNumDaysSince(selectedRun?.Date)}
          />
        )}
        {selectedTab === "Quality & Error" && (
          <RunQuality run={selectedRun as Run} />
        )}
        {selectedTab === "Base Composition" && (
          <RunBaseComposition run={selectedRun as Run} />
        )}
        {selectedTab === "Cycle Plots" && (
          <RunCyclePlots run={selectedRun as Run} />
        )}
        {selectedTab === "Polony Density Flow Cell" && (
          <PolonyDensity
            data={prodStatsData}
            isLoading={isLoading}
            isVitari={isVitari}
          />
        )}
        {selectedTab === "Cycle Chart" && (
          <CycleChart
            data={prodStatsData}
            reads={getReadsInOrder(selectedRun as Run)}
            isLoading={isLoading}
          />
        )}
        {selectedTab === "Cycle Data Flow Cell" && (
          <RunTileCycle
            data={prodStatsData}
            reads={getReadsInOrder(selectedRun as Run)}
            isLoading={isLoading}
          />
        )}
        {selectedTab === "Indexing Stats" && (
          <RunProdstatsIndexing
            data={prodStatsData}
            isLoading={isLoading}
            isDualLibrary={(selectedRun as Run)?.libraryPools === "2"}
          />
        )}
        {selectedTab === "Values Log" && (
          <RunValuesLog runID={selectedRun?.runID} />
        )}
        {selectedTab === "Run Logs" && typeRun !== "Spatial" && (
          <RunAthenaLogs
            runID={selectedRun?.runID}
            runDate={selectedRun?.Date}
          />
        )}
        {selectedTab === "Sensor Data" && (
          <RunMetric
            instrumentSerialNumber={selectedRun?.instrumentSerialNumber}
            runID={selectedRun?.runID}
            typeRun={typeRun}
          />
        )}
        {selectedTab === "Files" && selectedRun && (
          <RunLogs
            tenantID={selectedRun.tenantID}
            instrumentID={selectedRun.instrumentID}
            instrumentName={selectedRun.instrumentName}
            instrumentSerialNumber={selectedRun.instrumentSerialNumber}
            instrumentModel={
              selectedRun.model || selectedRun.instrumentModel
            }
            runID={selectedRun.runID}
          />
        )}
        {selectedTab === "Target Assignment Stats" && (
          <CytoTargetAssignmentStats run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Thumbnails" && (
          <RunThumbnails run={selectedRun as Run} />
        )}
        {selectedTab === "Well Thumbnails" && (
          <CytoRunThumbnail run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Segmentation Thumbnails" && (
          <CytoSegmentationThumbnail run={selectedRun as SpatialRun} />
        )}
        {selectedTab === "Instrument Report" && selectedRun && (
          <RunInstrumentReport
            tenantID={selectedRun.tenantID}
            instrumentID={selectedRun.instrumentID}
            instrumentSerialNumber={selectedRun.instrumentSerialNumber}
            instrumentName={selectedRun.instrumentName}
            runID={selectedRun.runID}
          />
        )}
        {selectedTab === "Values Log Charts" && (
          <ValuesChart run={selectedRun as Run} />
        )}
        {selectedTab === "Indexing" && (
          <RunIndexing runID={selectedRun?.runID} />
        )}
        {selectedTab === "System Stats" && (
          <RunSystemStats
            instrumentSerialNumber={selectedRun?.instrumentSerialNumber}
            runID={selectedRun?.runID}
            typeRun={typeRun}
          />
        )}
        {selectedTab === "Process Stats" && (
          <RunProcessStats
            instrumentSerialNumber={selectedRun?.instrumentSerialNumber}
            runID={selectedRun?.runID}
            typeRun={typeRun}
          />
        )}
        {selectedTab === "SSD Stats" && (
          <RunSSDStats
            instrumentSerialNumber={selectedRun?.instrumentSerialNumber}
            runID={selectedRun?.runID}
            typeRun={typeRun}
          />
        )}
        {selectedTab === "Notifications" && (
          <RunNotifications
            instrumentSerialNumber={selectedRun?.instrumentSerialNumber}
            runID={selectedRun?.runID}
          />
        )}
        {selectedTab === "Legacy Metrics" && (
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 50 }}
          >
            <LegacyPolonyDensity runID={selectedRun?.runID} />
            <RunTile runID={selectedRun?.runID} />
          </div>
        )}
      </div>
    );
  }
);
